import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { importAPI } from '../services/api';
import { ArrowLeft, Upload, FileText, CheckCircle, AlertCircle, Download } from 'lucide-react';
import Navbar from '../components/Navbar';

const Import = () => {
  const navigate = useNavigate();
  const [importType, setImportType] = useState('kunden'); // 'kunden' or 'anlagen'
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setPreview(null);
    setResult(null);

    // Generiere Vorschau
    try {
      const previewData = await importAPI.preview(selectedFile);
      setPreview(previewData);
    } catch (error) {
      console.error('Fehler bei Vorschau:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Lesen der Datei';
      alert(`Fehler beim Lesen der Datei: ${errorMessage}`);
      setPreview({ error: errorMessage });
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    setResult(null);

    try {
      let importResult;
      if (importType === 'kunden') {
        importResult = await importAPI.importKunden(file, updateExisting);
      } else {
        importResult = await importAPI.importAnlagen(file, updateExisting);
      }
      setResult(importResult);
    } catch (error) {
      console.error('Import fehlgeschlagen:', error);
      setResult({
        success: false,
        errors: [error.response?.data?.detail || 'Import fehlgeschlagen']
      });
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = (type) => {
    const templates = {
      kunden: `Kundennummer;Firma;Strasse;Land;PLZ;Ort;Telefon;Fax;Internet;Email;Ansprechpartner;Status
KD001;Musterfirma GmbH;Musterstraße 1;Deutschland;12345;Musterstadt;+49 123 456789;+49 123 456780;www.musterfirma.de;info@musterfirma.de;Max Mustermann;aktiv
KD002;Beispiel AG;Beispielweg 2;Deutschland;54321;Beispielstadt;+49 987 654321;;www.beispiel.de;kontakt@beispiel.de;Maria Beispiel;aktiv`,
      anlagen: `Kundennummer;LEC-Anlagennummer;Eigene Anlagennummer;Anlagenbezeichnung;Hersteller;Typ;Baujahr;Inventar-Nummer;Kostenstelle;Abteilung;Standort1;Standort2;Kältemittel;Kältemittelmenge;Inbetriebnahmedatum
KD001;LEC001;A001;Klimaanlage Bürogebäude;Daikin;VRV IV-S;2020;INV001;KST-100;Verwaltung;Musterstraße 1, 12345 Musterstadt;Gebäude A, Maschinenraum 1;R32;3.5;01.01.2020
KD002;LEC002;A002;Kälteanlage Lager;Carrier;30RB;2019;INV002;KST-200;Produktion;Beispielweg 2, 54321 Beispielstadt;Hauptgebäude, Kühlraum;R134a;8.0;15.03.2019`
    };

    const blob = new Blob([templates[type]], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vorlage_${type}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <button
          onClick={() => navigate('/kunden')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition"
        >
          <ArrowLeft className="w-5 h-5" />
          Zurück
        </button>

        <div className="bg-white rounded-xl shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Import aus VDKF-LEC</h1>
          <p className="text-gray-600 mb-8">
            Importieren Sie Kunden und Anlagen aus CSV oder Excel-Dateien
          </p>

          {/* Import Type Selection */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Was möchten Sie importieren?
            </label>
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setImportType('kunden');
                  setFile(null);
                  setPreview(null);
                  setResult(null);
                }}
                className={`flex-1 p-4 border-2 rounded-lg transition ${
                  importType === 'kunden'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-300 hover:border-blue-400'
                }`}
              >
                <div className="text-lg font-semibold text-gray-900">Kunden</div>
                <div className="text-sm text-gray-600 mt-1">
                  Kundenstammdaten importieren
                </div>
              </button>

              <button
                onClick={() => {
                  setImportType('anlagen');
                  setFile(null);
                  setPreview(null);
                  setResult(null);
                }}
                className={`flex-1 p-4 border-2 rounded-lg transition ${
                  importType === 'anlagen'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-300 hover:border-blue-400'
                }`}
              >
                <div className="text-lg font-semibold text-gray-900">Anlagen</div>
                <div className="text-sm text-gray-600 mt-1">
                  Anlagendaten importieren
                </div>
              </button>
            </div>
          </div>

          {/* Template Download */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-2">CSV-Vorlage herunterladen</h3>
                <p className="text-sm text-blue-800 mb-3">
                  Laden Sie eine Beispiel-CSV-Datei herunter, um das korrekte Format zu sehen.
                </p>
                <button
                  onClick={() => downloadTemplate(importType)}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition text-sm"
                >
                  <Download className="w-4 h-4" />
                  Vorlage für {importType === 'kunden' ? 'Kunden' : 'Anlagen'} herunterladen
                </button>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Datei auswählen (CSV oder Excel)
            </label>
            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-12 h-12 text-gray-400 mb-4" />
                  {file ? (
                    <div className="text-center">
                      <p className="text-sm font-semibold text-gray-700">{file.name}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {(file.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <p className="mb-2 text-sm text-gray-600">
                        <span className="font-semibold">Klicken Sie hier</span> oder ziehen Sie eine Datei
                      </p>
                      <p className="text-xs text-gray-500">CSV, XLSX oder XLS (max. 10MB)</p>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileSelect}
                />
              </label>
            </div>
          </div>

          {/* Preview */}
          {preview && !preview.error && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Vorschau der Datei</h3>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-700">
                  <strong>Spalten gefunden:</strong> {preview.columns.length}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Zeilen gesamt:</strong> {preview.row_count}
                </p>
                <div className="mt-3">
                  <p className="text-xs font-semibold text-gray-600 mb-1">Spalten:</p>
                  <div className="flex flex-wrap gap-2">
                    {preview.columns.map((col, idx) => (
                      <span key={idx} className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                        {col}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      {preview.columns.map((col, idx) => (
                        <th key={idx} className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {preview.preview.map((row, idx) => (
                      <tr key={idx}>
                        {preview.columns.map((col, colIdx) => (
                          <td key={colIdx} className="px-4 py-2 text-gray-900 whitespace-nowrap">
                            {row[col]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Update existing option */}
              <div className="mt-4 flex items-center gap-3">
                <input
                  type="checkbox"
                  id="updateExisting"
                  checked={updateExisting}
                  onChange={(e) => setUpdateExisting(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="updateExisting" className="text-sm text-gray-700">
                  Bestehende Einträge aktualisieren (statt überspringen)
                </label>
              </div>

              <button
                onClick={handleImport}
                disabled={importing}
                className="mt-6 w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition disabled:opacity-50 font-semibold"
              >
                {importing ? 'Importiere...' : `${preview.row_count} ${importType === 'kunden' ? 'Kunden' : 'Anlagen'} importieren`}
              </button>
            </div>
          )}

          {preview && preview.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-red-900 mb-1">Fehler beim Lesen der Datei</h3>
                  <p className="text-sm text-red-800">{preview.error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          {result && (
            <div className={`rounded-lg p-6 ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-start gap-3 mb-4">
                {result.success ? (
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <h3 className={`text-lg font-semibold ${result.success ? 'text-green-900' : 'text-red-900'}`}>
                    {result.success ? 'Import erfolgreich!' : 'Import mit Fehlern'}
                  </h3>
                  {result.imported !== undefined && (
                    <div className={`text-sm mt-1 ${result.success ? 'text-green-800' : 'text-red-800'}`}>
                      <p>
                        {result.imported} neu importiert
                        {result.updated > 0 && `, ${result.updated} aktualisiert`}
                        {' '}von {result.total} Einträgen
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {result.errors && result.errors.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-red-900 mb-2">Fehler:</h4>
                  <ul className="space-y-1">
                    {result.errors.slice(0, 10).map((error, idx) => (
                      <li key={idx} className="text-sm text-red-800">• {error}</li>
                    ))}
                    {result.errors.length > 10 && (
                      <li className="text-sm text-red-700 italic">
                        ... und {result.errors.length - 10} weitere Fehler
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {result.success && result.imported > 0 && (
                <button
                  onClick={() => navigate(importType === 'kunden' ? '/kunden' : '/')}
                  className="mt-4 bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition"
                >
                  Zur Übersicht
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Import;
